import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCountryConfig } from '../config/countries';
import i18n from '../i18n';

const CountryContext = createContext();

// Mapeamento de idiomas do navegador para países
const browserLanguageToCountry = {
  'pt-BR': 'BR',
  'pt': 'BR',
  'pt-PT': 'PT',
  'en-US': 'US',
  'en': 'US',
  'en-GB': 'GB',
  'es': 'ES',
  'es-ES': 'ES',
  'fr': 'FR',
  'fr-FR': 'FR',
  'it': 'IT',
  'it-IT': 'IT',
  'de': 'DE',
  'de-DE': 'DE',
  'nl': 'NL',
  'nl-NL': 'NL'
};

// Mapeamento de países para idiomas
const countryToLanguage = {
  'BR': 'pt',
  'PT': 'pt',
  'US': 'en',
  'GB': 'en',
  'ES': 'es',
  'FR': 'fr',
  'IT': 'it',
  'DE': 'de',
  'NL': 'nl'
};

export const CountryProvider = ({ children }) => {
  // Detecta o idioma inicial
  const detectInitialCountry = () => {
    // Tenta obter do localStorage primeiro
    const savedCountry = localStorage.getItem('country');
    if (savedCountry) {
      return savedCountry;
    }

    // Se não houver no localStorage, detecta do navegador
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLanguageToCountry[browserLang] || 'BR';
  };

  const [selectedCountry, setSelectedCountry] = useState(detectInitialCountry());
  const [countryConfig, setCountryConfig] = useState(getCountryConfig(selectedCountry));

  // Efeito para inicializar o idioma
  useEffect(() => {
    const country = selectedCountry;
    const language = countryToLanguage[country];
    
    // Atualiza o i18n
    i18n.changeLanguage(language);
    
    // Salva no localStorage
    localStorage.setItem('country', country);
    localStorage.setItem('language', language);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const newCountry = localStorage.getItem('country') || 'BR';
      setSelectedCountry(newCountry);
      setCountryConfig(getCountryConfig(newCountry));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const changeCountry = (countryCode) => {
    const country = getCountryConfig(countryCode);
    setSelectedCountry(countryCode);
    setCountryConfig(country);
    i18n.changeLanguage(countryToLanguage[countryCode]);
    localStorage.setItem('country', countryCode);
    localStorage.setItem('language', countryToLanguage[countryCode]);
  };

  return (
    <CountryContext.Provider value={{ selectedCountry, countryConfig, changeCountry }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}; 